import React, {
    useDeferredValue,
    useMemo
} from 'react';
import PropTypes from 'prop-types';

import useElementSize from '../hooks/useElementSize.js';
import {
    useIsExporting
} from '../hooks/useExport.js';

import {
    Wordcloud
} from '@visx/wordcloud';

import {
    scaleLinear
} from '@visx/scale';

import {
    Text
} from 'recharts';


const DEFAULT_HEIGHT = 420;

RIQWordCloud.propTypes = {
    words: PropTypes.arrayOf(PropTypes.object),
    height: PropTypes.number,
    onWordClick: PropTypes.func
};

const getBodyFont = _.memoize(() => {
    const style = window.getComputedStyle(document.body, null);
    const fontFamily = style.getPropertyValue('font-family');
    return fontFamily;
});

const createSVGElements = _.memoize(() => {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.style.position = 'absolute';
    svg.style.overflow = 'hidden';

    svg.setAttribute('width', 10000);
    svg.setAttribute('height', 10000);

    const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    text.style.fontFamily = getBodyFont();

    text.setAttributeNS(null, 'x', 0);
    text.setAttributeNS(null, 'y', 0);
    text.setAttributeNS(null, 'text-anchor', 'middle');

    const tspan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
    tspan.setAttributeNS(null, 'x', 0);
    tspan.setAttributeNS(null, 'dy', '0em');

    text.appendChild(tspan);
    svg.appendChild(text);

    return [svg, text, tspan];
});

const getTextBoundingClientRect = (word, fontSize, fontWeight, elements = createSVGElements(), shouldCleanup = true) => {
    const [svg, text, tspan] = elements;

    text.style.fontSize = fontSize;
    text.style.fontWeight = fontWeight;
    text.setAttributeNS(null, 'font-size', fontSize);
    text.setAttributeNS(null, 'font-weight', fontWeight);

    tspan.replaceChildren(document.createTextNode(word));

    if (!svg.isConnected) {
        document.body.appendChild(svg);
    }

    const boundingClientRect = text.getBoundingClientRect();

    if (shouldCleanup && svg.isConnected) {
        document.body.removeChild(svg);
    }

    return boundingClientRect;
};

// NOTE controls the minimum difference in points between min and max font sizes
const WORD_CLOUD_MIN_FONT_SIZE_DIFFERENCE = 5;
// NOTE controls how much space words can use to render relative available
const WORD_CLOUD_OCCUPANCY_MAX_THRESHOLD = 0.65;
// NOTE controls how much space words should use after rendering
const WORD_CLOUD_OCCUPANCY_MIN_THRESHOLD = 0.35;
// NOTE controls the maximum rendered width of a word relative available width
const WORD_CLOUD_TEXT_WIDTH_THRESHOLD = 0.8;
// NOTE controls the minimum number of words before the lower font size bound is increased
const WORD_CLOUD_MIN_WORDS_FOR_FONT_INCREASE = 20;

export default function RIQWordCloud({fontSize = 60, fontWeight = 400, height, onWordClick, onWordMouseOver, onWordMouseOut, padding = 2, words}) {

    height ||= DEFAULT_HEIGHT;
    const isExporting = useIsExporting();
    const {ref: containerRef, width} = useElementSize(isExporting);
    const deferredWidth = useDeferredValue(width);

    const [domain, wordsByTextLength] = useMemo(() => {
        const [min, max] = _.reduce(words, ([min, max], {value}) => {
            return [
                (value < min ? value : min),
                (value > max ? value : max)
            ];
        }, [Infinity, -Infinity]);

        return [
            (_.isFinite(min) ? [min, max] : undefined),
            _.sortBy(words, ({text}) => 0 - _.size(text))
        ];
    }, [words]);

    const fontScale = useMemo(() => {
        const renderableArea = deferredWidth * height;

        function getFontScale(fontSize, minFontSize = 10) {
            const scale = scaleLinear({
                domain: domain,
                range: [minFontSize, fontSize]
            });

            if (!deferredWidth || (fontSize - minFontSize <= WORD_CLOUD_MIN_FONT_SIZE_DIFFERENCE)) {
                return scale;
            }

            let area = 0;
            let occupancy = 0;
            let canRenderWords = true;
            const wordCount = _.size(wordsByTextLength);
            for (let word, i = 0; i < wordCount; i++) {
                word = wordsByTextLength[i];

                const fontSize = scale(word.value);
                const boundingClientRect = getTextBoundingClientRect(word.text, fontSize, fontWeight);
                const textWidth = boundingClientRect.width;
                // NOTE if the word is too long to render given width, bail
                if (textWidth / deferredWidth > WORD_CLOUD_TEXT_WIDTH_THRESHOLD) {
                    canRenderWords = false;
                    break;
                }

                area += (boundingClientRect.height * boundingClientRect.width);
                occupancy = area / renderableArea;
                // NOTE if we've exceeded our occupancy threshold, bail
                if (occupancy > WORD_CLOUD_OCCUPANCY_MAX_THRESHOLD) {
                    canRenderWords = false;
                    break;
                }
            }

            if (canRenderWords && wordCount >= WORD_CLOUD_MIN_WORDS_FOR_FONT_INCREASE && occupancy < WORD_CLOUD_OCCUPANCY_MIN_THRESHOLD) {
                return getFontScale(fontSize, minFontSize + 1);
            }

            return (!canRenderWords ? getFontScale(fontSize - 1, minFontSize) : scale);
        }

        return getFontScale(fontSize);
    }, [deferredWidth, domain, fontSize, fontWeight, height, wordsByTextLength]);

    const fontSizeSetter = (datum) => fontScale(datum.value);
    const fixedValueGenerator = () => 0.5;

    const handleMouseOver = (event, word) => {
        if (onWordMouseOver) {

            const textBBox = event.target.getBBox();

            const svgBoundingBox = event.target.ownerSVGElement.getBoundingClientRect();
            const wordBoundingBox = event.target.getBoundingClientRect();

            const wordBounds = {
                x: wordBoundingBox.x - svgBoundingBox.x,
                y: wordBoundingBox.y - svgBoundingBox.y,
                globalX: wordBoundingBox.x + window.scrollX,
                globalY: wordBoundingBox.y + window.scrollY,
                width: textBBox.width,
                height: textBBox.height
            };

            onWordMouseOver(word, wordBounds);
        }
    };

    return (
        <div
            className='tlw-flex tlw-h-full tlw-w-full tlw-items-center tlw-py-5'
            ref={containerRef}
        >
            {deferredWidth > 0 &&
                <Wordcloud
                    height={height}
                    width={deferredWidth}
                    words={words}
                    font={getBodyFont()}
                    fontSize={fontSizeSetter}
                    fontWeight={fontWeight}
                    spiral='archimedean'
                    rotate={0}
                    padding={padding}
                    random={fixedValueGenerator}
                >
                    { (words) => {
                        return words.map((word, idx) => {
                            const {font, rotate, size, text, value, weight, x, y} = word;
                            return <Text
                                key={`${idx}-${text}-${value}`}
                                textAnchor={'middle'}
                                transform={`translate(${x}, ${y}) rotate(${rotate})`}
                                font={font}
                                fontSize={size}
                                fontWeight={weight}
                                // set fill color with tailwind className tlw-fill-...
                                fill=''
                                onMouseOver={event => handleMouseOver(event, word)}
                                onMouseOut={() => onWordMouseOut && onWordMouseOut()}
                                onClick={() => onWordClick && onWordClick(word)}
                                className={`tlw-cursor-pointer ${word.className ?? ''}`}
                            >{text}</Text>;
                        });
                    }}
                </Wordcloud>
            }
        </div>
    );
}
